// "Next training" — the soonest session on the viewer's journey.
import { useT } from '../lib/i18n';
import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus, ExternalLink, GraduationCap, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import type { MySession } from '@/services/learningService';
import { formatSessionRange, localDayOf, sessionZoneNote } from '../lib/format';
import { selfAttendanceAction } from '../lib/attendance';
import { AssignmentTypeBadge } from './AssignmentTypeBadge';
import { AttendanceChip } from './AttendanceChip';
import { ReasonLine } from './ReasonLine';

interface Props {
  session: MySession;
  onView: (s: MySession) => void;
  /** Phase 3: self check-in (the page owns the mutation). Shown only inside the check-in window. */
  onCheckIn?: (s: MySession) => void;
  checkingIn?: boolean;
}

export const NextTrainingCard: React.FC<Props> = ({ session, onView, onCheckIn, checkingIn }) => {
  const t = useT();
  const canCheckIn = !!onCheckIn && selfAttendanceAction(session.starts_at, session.ends_at, session.status, session.me?.attendance) === 'checkin';
  const joinUrl = session.meeting_url && /^https?:\/\//i.test(session.meeting_url) ? session.meeting_url : null;
  const zone = sessionZoneNote(session.timezone);
  return (
    <Card data-testid="next-training-card" className="border-orange-300/60 dark:border-orange-500/30">
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-orange-600 dark:text-orange-400">
          <GraduationCap className="w-4 h-4" aria-hidden /> {t('training:next_training', 'Next training')}
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-foreground">{session.title}</h2>
              <AssignmentTypeBadge type={session.me?.assignment_type} />
              {session.me?.attendance ? <AttendanceChip status={session.me.attendance} /> : null}
            </div>
            {session.program_name && <div className="text-sm text-muted-foreground">{session.program_name}</div>}
            <div className="text-sm text-foreground">
              {formatSessionRange(session.starts_at, session.ends_at, undefined, session.timezone)}
              {zone && <span className="ml-1 text-xs text-muted-foreground">· {zone}</span>}
            </div>
            <ReasonLine type={session.me?.assignment_type} reason={session.me?.reason} />
          </div>
          <div className="flex flex-wrap gap-2">
            {canCheckIn && (
              <Button size="sm" disabled={checkingIn} onClick={() => onCheckIn?.(session)} data-testid="next-checkin">
                {checkingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:att_checkin', 'Check in')}
              </Button>
            )}
            <Button size="sm" variant={canCheckIn ? 'outline' : 'default'} onClick={() => onView(session)}>{t('training:view_session', 'View session')}</Button>
            {joinUrl && (
              <Button size="sm" variant="outline" asChild>
                <a href={joinUrl} target="_blank" rel="noreferrer">{t('training:join_meeting', 'Join meeting')} <ExternalLink className="ml-1 h-3.5 w-3.5" aria-hidden /></a>
              </Button>
            )}
            <Button size="sm" variant="ghost" asChild>
              <Link to={`../my-calendar?date=${localDayOf(session.starts_at)}`}><CalendarPlus className="mr-1 h-4 w-4" aria-hidden />{t('training:add_to_calendar', 'Add to calendar')}</Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NextTrainingCard;
